import React, { useState, useEffect, useRef } from 'react';
import { Plus, Mail, Globe, Train } from 'lucide-react';
import { queryParkingRegulations } from './api/sweepAPI';
import WeeklyPreview from './components/WeeklyPreview';
import { geocodeAddress } from './api/geocodeUtils';
import { type Address } from './shared/types';

const App: React.FC = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [newAddress, setNewAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    
    console.log('Initializing Google Maps...');
    
    const checkGoogleMaps = () => {
      console.log(`Checking Google Maps (attempt ${retryCount + 1}/${maxRetries})`);
      if (window.google?.maps && window.google.maps.places) {
        console.log('Google Maps and Places API loaded successfully');
        setIsGoogleLoaded(true);
        setTimeout(() => {
          if (inputRef.current) {
            console.log('Initializing autocomplete for input:', inputRef.current);
            const nycBounds = new google.maps.LatLngBounds(
              { lat: 40.477399, lng: -74.259090 },
              { lat: 40.917577, lng: -73.700272 }
            );

            try {
              autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
                bounds: nycBounds,
                strictBounds: true,
                componentRestrictions: { country: "us" },
                fields: ['formatted_address', 'geometry', 'place_id']
              });
              console.log('Autocomplete initialized successfully');
              
              autocompleteRef.current?.addListener('place_changed', handlePlaceChanged);
            } catch (error) {
              console.error('Error initializing autocomplete:', error);
            }
          } else {
            console.error('Input ref not available');
          }
        }, 100);
        return;
      }
      
      if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(checkGoogleMaps, 1000);
      } else {
        console.error('Google Maps not loaded!');
        setError('Google Maps failed to initialize. Please refresh the page.');
        fetch('/api/report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'MAPS_LOAD_ERROR',
            message: 'Google Maps failed to initialize',
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
          })
        }).catch(console.error);
      }
    };
    
    checkGoogleMaps();
  }, []);

  const handlePlaceChanged = () => {
    console.log('Place changed event triggered');
    if (!autocompleteRef.current) {
      console.warn('Autocomplete ref is not initialized');
      return;
    }

    const place = autocompleteRef.current.getPlace();
    console.log('Selected place:', place);
    if (place && place.geometry && place.formatted_address) {
      console.log('Setting new address:', place.formatted_address);
      setNewAddress(place.formatted_address);
    } else {
      console.error("No geometry or formatted address found for selected place.");
      setError("Invalid place selected");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAddress(e.target.value);
  };

  const handleAddAddress = async () => {
    console.log('Adding new address:', newAddress);
    setError(null);
    setIsLoading(true);

    if (!newAddress) {
      console.warn('No address provided');
      setError('Please enter a valid address.');
      setIsLoading(false);
      return;
    }

    try {
      const geocoded = await geocodeAddress(newAddress);
      const [, borough] = newAddress.split(',').map(s => s.trim());

      const regulations = await queryParkingRegulations({
        street: geocoded.street,
        borough,
        geocoded: {
          lat: geocoded.lat,
          lng: geocoded.lng
        }
      });

      const newAddr: Address = {
        id: Date.now(),
        address: newAddress,
        regulations: regulations.map(reg => ({
          ...reg,
          displayText: `${reg.fromStreet} to ${reg.toStreet} - ${reg.schedule.days.join('/')} ${reg.schedule.startTime || 'Midnight'}-${reg.schedule.endTime || '3AM'}`
        }))
      };

      setAddresses(prev => [...prev, newAddr]);
      setNewAddress('');
    } catch (err) {
      console.error('Error adding address:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isGoogleLoaded && !error) {
    return (
      <div className="min-h-screen bg-mta-black w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-mta-yellow border-t-transparent rounded-full animate-spin"></div>
          <span className="text-mta-yellow font-helvetica font-bold uppercase tracking-wider">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mta-black w-full">
      <div className="w-full bg-mta-black border-b-4 border-mta-yellow">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-14 h-14 bg-mta-yellow rounded-full">
              <Train className="w-8 h-8 text-mta-black" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-mta-white font-helvetica uppercase tracking-tight">
                NYC ASP Calendar
              </h1>
              <p className="text-mta-silver text-sm font-helvetica uppercase tracking-wide">
                Alternate Side Parking Tracker
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="card-brutalist mb-8">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-mta-blue rounded-full flex items-center justify-center">
                <span className="text-mta-white font-bold text-sm">1</span>
              </div>
              <h2 className="text-lg font-bold text-mta-white uppercase tracking-wide font-helvetica">
                Enter Your Address
              </h2>
            </div>
            <p className="text-mta-silver text-sm ml-11">
              Search for any NYC street to see cleaning schedules
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-mta-red text-mta-white border-3 border-mta-white font-helvetica">
              <span className="font-bold uppercase">Error: </span>{error}
            </div>
          )}

          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <input
                ref={inputRef}
                type="text"
                value={newAddress}
                onChange={handleInputChange}
                placeholder="ENTER STREET ADDRESS..."
                className="input-brutalist w-full h-14 text-base uppercase tracking-wide placeholder:text-mta-gray"
                disabled={isLoading}
                autoComplete="off"
                spellCheck="false"
              />
            </div>
            <button
              onClick={handleAddAddress}
              disabled={isLoading}
              className={`btn-brutalist btn-mta-yellow h-14 flex items-center justify-center gap-2 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-mta-black border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <Plus className="w-5 h-5" strokeWidth={3} />
                  <span className="hidden sm:inline">Add</span>
                </>
              )}
            </button>
          </div>
        </div>

        <div className="sm:hidden bg-mta-blue border-3 border-mta-white p-4 mb-6 text-sm font-helvetica">
          <span className="font-bold uppercase">Note: </span>
          For the best calendar experience, use a desktop browser.
        </div>

        {addresses.length > 0 && <WeeklyPreview addresses={addresses} setAddresses={setAddresses} />}
      </div>

      <footer className="border-t-4 border-mta-yellow bg-mta-black mt-auto">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-6">
              <div className="w-10 h-10 bg-mta-green rounded-full flex items-center justify-center">
                <span className="text-mta-white font-bold">G</span>
              </div>
              <div className="w-10 h-10 bg-mta-orange rounded-full flex items-center justify-center">
                <span className="text-mta-white font-bold">F</span>
              </div>
              <div className="w-10 h-10 bg-mta-blue rounded-full flex items-center justify-center">
                <span className="text-mta-white font-bold">A</span>
              </div>
            </div>
            
            <p className="text-mta-silver text-sm text-center font-helvetica">
              Built with{' '}
              <a 
                href="https://data.cityofnewyork.us/Transportation/Parking-Regulation-Locations-and-Signs/nfid-uabd/about_data" 
                className="text-mta-yellow hover:underline font-bold" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                NYC OPEN DATA
              </a>
            </p>
            
            <p className="text-mta-gray text-xs text-center font-helvetica uppercase tracking-wide">
              Always verify rules with posted street signs
            </p>
            
            <div className="flex items-center gap-6 mt-2">
              <a 
                href="mailto:mattalldian@gmail.com" 
                className="text-mta-silver hover:text-mta-yellow transition-colors"
              >
                <Mail className="w-5 h-5" />
              </a>
              <a 
                href="https://www.buymeacoffee.com/XYaobWfhi" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-brutalist btn-mta-yellow !py-2 !px-4 text-xs"
              >
                Buy Me A Coffee
              </a>
              <a 
                href="https://mattalldian.com" 
                className="text-mta-silver hover:text-mta-yellow transition-colors"
              >
                <Globe className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
